// ============================================================================
// APP METADATA
// ============================================================================
export const APP_NAME = 'iConcreteElement';
export const APP_VERSION = '1.0.0';
export const APP_VERSION_POLICY = { currentVersion: APP_VERSION };
export { SUPPORT_TYPES, ORIENTATION_TYPES, DIF_TYPES } from '../shared';

// ============================================================================
// TYPES
// ============================================================================

export type APIPayload = {
  title: string;
  L: number;
  W: string;
  wadd: string;
  supports: number;
  orientation: number;
  sectionType: number;
  b: number;
  h: number;
  tf: number;
  tw: number;
  rebar: string;
  rebarDepth: string;
  shearRebar: string;
  fy: string;
  fc: string;
  dif: number;
  axialLoad: string;
  Pa: string; // pressure history csv
  td: string; // time history csv
  d0: number; // initial displacement
  v0: number; // initial velocity
};

export type ChartKey = 'resistance' | 'load' | 'displacement' | 'rotation' | 'reaction' | 'bendingMoment' | 'section' | 'cross_section';

export interface ChartImage {
  mime: 'image/png';
  base64: string; // base64 WITHOUT the data: prefix
}

export interface APIResponse {
  // Scalar Results
  TA: number;
  Me: number;
  Xmax: number;
  tmax: number;
  Xmin: number;
  tmin: number;
  rebar_weight: number;
  GWP: number;
  concrete_volume: number;

  // Section Properties
  Ec: number;

  // History / Array Data
  Ru: number[];
  ru: number[];
  xe: number[];
  duct: number[];
  rot: number[];
  V1: number[];
  V2: number[];
  K: number[];
  Tn: number[];

  // Charts (optional + partial means "some keys may be missing")
  charts?: Partial<Record<ChartKey, ChartImage>>;
}

// Dropdown option for section types
export const SECTION_TYPES: DropdownOption[] = [
  { value: 0, label: 'Rectangular' },
  { value: 1, label: 'Circular' },
  { value: 2, label: 'Tee' }
];

import type { DropdownOption } from '../shared';
// ============================================================================
// RESPONSE DISPLAY CONFIGURATION
// ============================================================================

import type { SectionConfig } from '../shared';
import { createElement as h } from 'react';

/**
 * Configuration for displaying response data
 * Defines what fields to show and how to format them
 */
export const RESPONSE_DISPLAY_CONFIG: SectionConfig<APIResponse>[] = [
  {
    title: 'Results',
    fields: [
      { key: 'TA', label: 'Tributary Area', getValue: data => h('span', null, data.TA.toFixed(2), ' ft', h('sup', null, '2')) },
      {
        key: 'Me',
        label: 'Effective Mass',
        getValue: data => h('span', null, data.Me.toFixed(3), ' lb-sec', h('sup', null, '2'), '/in')
      },
      {
        label: 'Period (Reb / Inb)',
        getValue: data => `[${data.Tn.map(value => value.toFixed(2)).join(', ')}] msec`
      },
      {
        label: 'Resistance (Reb / Inb)',
        getValue: data => `[${data.Ru.map(value => value.toFixed(2)).join(', ')}] kips`
      },
      {
        label: 'Ductility (Reb / Inb)',
        getValue: data => `[${data.duct.map(value => value.toFixed(2)).join(', ')}]`
      },
      {
        label: 'Rotation (Reb / Inb)',
        getValue: data => `[${data.rot.map(value => value.toFixed(2)).join(', ')}]`
      },
      {
        label: 'Max Displacement',
        getValue: data => `${data.Xmax.toFixed(2)} in @ ${data.tmax.toFixed(2)} msec`
      },
      {
        label: 'Min Displacement',
        getValue: data => `${data.Xmin.toFixed(2)} in @ ${data.tmin.toFixed(2)} msec`
      },
      {
        label: 'Reaction Support 1',
        getValue: data => `[${data.V1.map(value => value.toFixed(2)).join(', ')}] kips`
      },
      {
        label: 'Reaction Support 2',
        getValue: data => `[${data.V2.map(value => value.toFixed(2)).join(', ')}] kips`
      }
    ]
  },
  {
    title: 'Section Properties',
    fields: [
      {
        key: 'rebar_weight',
        label: 'Rebar Weight',
        getValue: data => h('span', null, data.rebar_weight.toFixed(2), ' lbs/ft')
      },
      {
        key: 'GWP',
        label: 'GWP',
        getValue: data => h('span', null, data.GWP.toFixed(2), ' kg CO', h('sub', null, '2'), 'e')
      }
    ]
  }
];

// ============================================================================
// URL PARAMETER MAPPING
// ============================================================================

/**
 * Mapping from URL parameter names to form field names
 * Used for parsing ?inp={...} query params
 */
export const URL_FIELD_MAP: Record<string, string> = {
  title: 'title',
  L: 'length',
  supports: 'supports',
  W: 'tributaryWidth',
  wadd: 'addedWeight',
  orientation: 'orientation',
  sectionType: 'sectionType',
  b: 'b',
  h: 'h',
  tf: 'tf',
  tw: 'tw',
  rebar: 'rebar',
  rebarDepth: 'rebarDepth',
  shearRebar: 'shearRebar',
  fy: 'fy',
  fc: 'fc',
  dif: 'dif',
  P: 'axialLoad',
  Pa: 'pressureHistory',
  td: 'timeHistory',
  d0: 'u0',
  v0: 'v0'
};

/**
 * Value mappings for URL params (e.g., '0' -> 'pin-pin')
 */
export { URL_VALUE_MAPS } from '../shared';
